import * as Sqrl from 'squirrelly';
import {getSizedImageUrl} from '@shopify/theme-images';

import {getUrlWithVariant} from '../vendor/theme-scripts/theme-product-form';
import {fetchProduct} from '../util/fetch-product';
import loadScript from '../util/loader';

import Tooltip from './tooltip';
import NativeScrollbar from './native-scrollbar';

const defaults = {
  color: 'ash',
};

const selectors = {
  formGridSwatch: '[data-grid-swatch-form]',
  swatch: 'data-swatch',
  outerGrid: '[data-product-grid-item]',
  slide: '[data-grid-slide]',
  image: 'data-swatch-image',
  variant: 'data-swatch-variant',
  button: '[data-swatch-button]',
  link: '[data-grid-link]',
  wrapper: '[data-grid-swatches]',
  template: '[data-swatch-template]',
  handle: 'data-swatch-handle',
  label: 'data-swatch-label',
  tooltip: 'data-tooltip',
  swatchCount: 'data-swatch-count',
  scrollbar: 'data-scrollbar',
};

const classes = {
  visible: 'is-visible',
  stopEvents: 'no-events',
};

class ColorMatch {
  constructor(options = {}) {
    this.settings = {
      ...defaults,
      ...options,
    };

    this.match = this.init();
  }

  getColor() {
    return this.match;
  }

  init() {
    const getColors = loadScript({json: window.theme.assets.swatches});
    return getColors
      .then((colors) => {
        return this.matchColors(colors, this.settings.color);
      })
      .catch((e) => {
        console.log('failed to load swatch colors script');
        console.log(e);
      });
  }

  matchColors(colors, name) {
    let bg = '#E5E5E5';
    let img = null;
    const path = window.theme.assets.base || '/';
    const comparisonName = name.toLowerCase().replace(/\s/g, '');
    const array = colors.colors;

    if (array) {
      let indexArray = null;

      const hexColorArr = array.filter((colorObj, index) => {
        const neatName = Object.keys(colorObj).toString().toLowerCase().replace(/\s/g, '');

        if (neatName === comparisonName) {
          indexArray = index;

          return colorObj;
        }
      });

      if (hexColorArr.length && indexArray !== null) {
        const value = Object.values(array[indexArray])[0];
        bg = value;

        if (value.includes('.jpg') || value.includes('.jpeg') || value.includes('.png') || value.includes('.svg')) {
          img = `${path}${value}`;
          bg = '#888888';
        }
      }
    }

    return {
      color: this.settings.color,
      path: img,
      hex: bg,
    };
  }
}

class Swatch {
  constructor(element) {
    this.element = element;
    this.colorString = element.getAttribute(selectors.swatch);
    this.image = element.getAttribute(selectors.image);
    this.variant = element.getAttribute(selectors.variant);
    const matcher = new ColorMatch({color: this.colorString});
    matcher.getColor().then((result) => {
      this.colorMatch = result;
      this.init();
    });
  }

  init() {
    this.setStyles();
    if (this.variant) {
      this.handleEvents();
    }
  }

  setStyles() {
    if (this.colorMatch.hex) {
      this.element.style.setProperty('--swatch', `${this.colorMatch.hex}`);
    }
    if (this.colorMatch.path) {
      this.element.style.setProperty('background-image', `url(${this.colorMatch.path})`);
      this.element.style.setProperty('background-size', 'cover');
      this.element.style.setProperty('background-position', 'center center');
    }
  }

  handleEvents() {
    this.outer = this.element.closest(selectors.outerGrid);
    if (this.outer) {
      this.slide = this.outer.querySelector(selectors.slide);

      this.linkElement = this.outer.querySelector(selectors.link);
      this.linkElementAll = this.outer.querySelectorAll(selectors.link);
      this.linkDestination = getUrlWithVariant(this.linkElement.getAttribute('href'), this.variant);
      this.button = this.element.closest(selectors.button);

      if (this.button.closest(selectors.formGridSwatch)) {
        this.button.addEventListener(
          'mouseenter',
          function () {
            this.changeImage();
          }.bind(this)
        );
      }

      if (!this.button.closest(selectors.formGridSwatch)) {
        this.button.addEventListener(
          'click',
          function () {
            this.changeImage();
          }.bind(this)
        );
      }
    }
  }

  changeImage() {
    this.linkElementAll.forEach((link) => {
      link.setAttribute('href', this.linkDestination);
    });

    this.slide.setAttribute('src', this.linkDestination);
    if (this.image) {
      // container width rounded to the nearest 180 pixels
      // increses likelihood that the image will be cached
      let widthRounded = Math.ceil(this.slide.offsetWidth / 180) * 180;
      let sizedImage = getSizedImageUrl(this.image, `${widthRounded}x`);
      window
        .fetch(sizedImage)
        .then((response) => {
          return response.blob();
        })
        .then((blob) => {
          var objectURL = URL.createObjectURL(blob);
          this.slide.style.setProperty('background-color', '#fff');
          this.slide.style.setProperty('background-image', `url("${objectURL}")`);
        })
        .catch((error) => {
          console.log(`Error: ${error}`);
        });
    }
  }
}

class GridSwatch {
  constructor(wrap, container) {
    this.counterSwatches = wrap.parentNode.previousElementSibling;
    this.template = document.querySelector(selectors.template).innerHTML;
    this.wrap = wrap;
    this.container = container;
    this.handle = wrap.getAttribute(selectors.handle);
    const label = wrap.getAttribute(selectors.label).trim().toLowerCase();
    fetchProduct(this.handle).then((product) => {
      this.product = product;
      this.colorOption = product.options.find(function (element) {
        return element.name.toLowerCase() === label || null;
      });

      if (this.colorOption) {
        this.swatches = this.colorOption.values;
        this.init();
      }
    });
  }

  init() {
    this.wrap.innerHTML = '';
    this.count = 0;

    this.swatches.forEach((swatch) => {
      let variant = this.product.variants.find((variant) => {
        return variant.options.includes(swatch);
      });

      if (variant) {
        this.count++;
        const image = variant.featured_media ? variant.featured_media.preview_image.src : '';

        this.wrap.innerHTML += Sqrl.render(this.template, {
          color: swatch,
          uniq: `${this.product.id}-${variant.id}`,
          variant: variant.id,
          available: variant.available,
          image,
        });
      }
    });

    this.swatchElements = this.wrap.querySelectorAll(`[${selectors.swatch}]`);

    if (this.counterSwatches.hasAttribute(selectors.swatchCount)) {
      this.counterSwatches.innerText = `${this.count} ${this.count > 1 ? theme.strings.otherColor : theme.strings.oneColor}`;

      this.counterSwatches.addEventListener('mouseenter', () => {
        this.wrap.closest(selectors.link).classList.add(classes.stopEvents);
        this.counterSwatches.nextElementSibling.classList.add(classes.visible);
      });

      this.counterSwatches.closest(selectors.outerGrid).addEventListener('mouseleave', () => {
        this.wrap.closest(selectors.link).classList.remove(classes.stopEvents);
        this.counterSwatches.nextElementSibling.classList.remove(classes.visible);
      });
    }

    if (this.wrap.hasAttribute(selectors.scrollbar)) {
      new NativeScrollbar(this.wrap);
    }

    this.swatchElements.forEach((el) => {
      new Swatch(el);
      const tooltipEl = el.closest(`[${selectors.tooltip}]`);

      if (tooltipEl) {
        new Tooltip(tooltipEl);
      }
    });
  }
}

const makeGridSwatches = (section) => {
  const gridSwatchWrappers = section.container.querySelectorAll(selectors.wrapper);
  gridSwatchWrappers.forEach((wrap) => {
    new GridSwatch(wrap, this);
  });
};

const swatchSection = {
  onLoad() {
    this.swatches = [];
    const els = this.container.querySelectorAll(`[${selectors.swatch}]`);
    els.forEach((el) => {
      this.swatches.push(new Swatch(el));
    });
  },
};

const swatchGridSection = {
  onLoad() {
    makeGridSwatches(this);
  },
};

export {swatchGridSection, swatchSection, makeGridSwatches, Swatch};
